/**
 * First-run onboarding wizard for newly-created HoodDesk societies.
 *
 * Triggered by `router.tsx` when an admin lands on `/` and the active
 * Society row has `onboarding_completed_at` NULL. Five sequential, fully
 * skippable steps capture the essentials:
 *
 *   1. society_basics  — name + address + city + timezone + contact email
 *   2. blocks_units    — bulk-create blocks and units (CSV paste or grid)
 *   3. charge_types    — confirm or edit the 6 default charge types
 *   4. first_admin     — (optional) info-only nudge to invite a second admin
 *   5. completed       — quick-link landing page; Finish POSTs /complete
 *
 * Every step round-trips its progress to the server via
 * `PATCH /v1/society/me/onboarding/step` so an interrupted admin can pick
 * up where they left off on the next login. The final "Finish" button hits
 * `POST /v1/society/me/onboarding/complete`, which flips the
 * `onboarding_completed_at` timestamp — subsequent logins skip the wizard
 * and land on /dashboard normally.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  ClipboardList,
  Loader2,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Wallet
} from 'lucide-react';
import { get, patch, post, getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { LogoMark } from '@/components/brand/Logo';
import { cn } from '@/lib/utils';

type StepKey = 'society_basics' | 'blocks_units' | 'charge_types' | 'first_admin' | 'completed';

interface SocietyMe {
  id: string;
  name: string;
  city?: string | null;
  state?: string | null;
  address_line1?: string | null;
  timezone: string;
  onboarding_completed_at: string | null;
  onboarding_step: StepKey | null;
}

interface Block {
  id: string;
  name: string;
  description: string | null;
}

interface ChargeTypeRow {
  id: string;
  code: string;
  label: string;
  default_amount_inr: number;
  active: boolean;
  sort_order: number;
}

const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Karachi',
  'Asia/Dhaka',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'UTC'
];

interface StepDef {
  key: StepKey;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}
const STEPS: ReadonlyArray<StepDef> = [
  { key: 'society_basics', label: 'Society basics', icon: Building2 },
  { key: 'blocks_units', label: 'Blocks & units', icon: ClipboardList },
  { key: 'charge_types', label: 'Charge types', icon: Wallet },
  { key: 'first_admin', label: 'First admin', icon: UserPlus },
  { key: 'completed', label: 'Finish', icon: Sparkles }
];

function stepIndex(key: StepKey | null | undefined): number {
  const idx = STEPS.findIndex((s) => s.key === key);
  return idx < 0 ? 0 : idx;
}

export function OnboardingPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const societyQuery = useQuery({
    queryKey: ['society', 'me'],
    queryFn: () => get<SocietyMe>('/society/me')
  });

  // Resume from the server's last-known step on first paint, then track it
  // locally so Back/Next don't ping the API on every click.
  const [active, setActive] = useState<StepKey>('society_basics');
  useEffect(() => {
    if (societyQuery.data?.onboarding_step && societyQuery.data.onboarding_step !== 'completed') {
      setActive(societyQuery.data.onboarding_step);
    }
  }, [societyQuery.data?.onboarding_step]);

  const persistStep = useMutation({
    mutationFn: (step: StepKey) => patch<SocietyMe, { step: StepKey }>('/society/me/onboarding/step', { step })
  });

  const finishOnboarding = useMutation({
    mutationFn: () => post<SocietyMe>('/society/me/onboarding/complete'),
    onSuccess: () => {
      toast.success('Onboarding complete');
      void qc.invalidateQueries({ queryKey: ['society'] });
      void navigate({ to: '/dashboard' });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  function goTo(next: StepKey): void {
    setActive(next);
    persistStep.mutate(next);
  }

  function nextStep(): void {
    const idx = stepIndex(active);
    const target = STEPS[idx + 1]?.key;
    if (target) goTo(target);
  }

  function prevStep(): void {
    const idx = stepIndex(active);
    const target = STEPS[idx - 1]?.key;
    if (target) goTo(target);
  }

  const currentIdx = stepIndex(active);
  const progressPct = ((currentIdx + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-vt-bg">
      <header className="border-b border-vt-border bg-vt-surface">
        <div className="mx-auto flex w-full max-w-[1100px] items-center justify-between px-vt-6 py-vt-4">
          <div className="flex items-center gap-vt-3">
            <LogoMark />
            <div>
              <p className="text-vt-12 uppercase tracking-wide text-vt-text-muted">Setup wizard</p>
              <h1 className="text-vt-18 font-semibold text-vt-text">Welcome to HoodDesk</h1>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void navigate({ to: '/dashboard' })}
            title="Skip wizard and go straight to the dashboard. You can resume from Settings later."
          >
            Skip for now
          </Button>
        </div>
        <div className="h-1.5 w-full bg-vt-surface-2">
          <div
            className="h-full bg-vt-primary transition-[width] duration-300"
            style={{ width: `${progressPct}%` }}
            aria-label={`Step ${currentIdx + 1} of ${STEPS.length}`}
          />
        </div>
        <nav className="mx-auto flex w-full max-w-[1100px] items-center gap-vt-2 overflow-x-auto px-vt-6 py-vt-3">
          {STEPS.map((s, idx) => {
            const isActive = s.key === active;
            const isDone = idx < currentIdx;
            const Icon = s.icon;
            return (
              <div
                key={s.key}
                className={cn(
                  'flex items-center gap-vt-2 rounded-vt-sm border px-vt-3 py-vt-1 text-vt-13',
                  isActive
                    ? 'border-vt-primary bg-vt-primary-100 text-vt-primary'
                    : isDone
                      ? 'border-vt-safe/40 bg-vt-safe/10 text-vt-safe'
                      : 'border-vt-border bg-vt-surface text-vt-text-muted'
                )}
              >
                {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                <span className="whitespace-nowrap">
                  {idx + 1}. {s.label}
                </span>
              </div>
            );
          })}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-[1100px] px-vt-6 py-vt-6">
        {societyQuery.isLoading ? (
          <div className="flex items-center justify-center py-vt-12">
            <Loader2 className="h-6 w-6 animate-spin text-vt-text-muted" />
          </div>
        ) : active === 'society_basics' ? (
          <StepSocietyBasics society={societyQuery.data} onNext={nextStep} onSkip={nextStep} />
        ) : active === 'blocks_units' ? (
          <StepBlocksUnits onNext={nextStep} onSkip={nextStep} onBack={prevStep} />
        ) : active === 'charge_types' ? (
          <StepChargeTypes onNext={nextStep} onSkip={nextStep} onBack={prevStep} />
        ) : active === 'first_admin' ? (
          <StepFirstAdmin onNext={nextStep} onSkip={nextStep} onBack={prevStep} />
        ) : (
          <StepFinish
            societyName={societyQuery.data?.name ?? 'your society'}
            onFinish={() => finishOnboarding.mutate()}
            finishing={finishOnboarding.isPending}
            onBack={prevStep}
            onNavigate={(to) => void navigate({ to })}
          />
        )}
      </main>
    </div>
  );
}

// ─── Step 1 — Society basics ──────────────────────────────────────────────

interface BasicsForm {
  name: string;
  address_line1: string;
  city: string;
  state: string;
  timezone: string;
  contact_email: string;
  contact_phone: string;
}

function StepSocietyBasics({
  society,
  onNext,
  onSkip
}: {
  society: SocietyMe | undefined;
  onNext: () => void;
  onSkip: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [form, setForm] = useState<BasicsForm>({
    name: society?.name ?? '',
    address_line1: society?.address_line1 ?? '',
    city: society?.city ?? '',
    state: society?.state ?? '',
    timezone: society?.timezone ?? 'Asia/Kolkata',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    if (society) {
      setForm((prev) => ({
        ...prev,
        name: society.name,
        address_line1: society.address_line1 ?? '',
        city: society.city ?? '',
        state: society.state ?? '',
        timezone: society.timezone
      }));
    }
  }, [society]);

  const save = useMutation({
    mutationFn: () =>
      patch<SocietyMe, Partial<BasicsForm>>('/society/me', {
        name: form.name.trim() || undefined,
        address_line1: form.address_line1.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        timezone: form.timezone || undefined,
        contact_email: form.contact_email.trim() || undefined,
        contact_phone: form.contact_phone.trim() || undefined
      }),
    onSuccess: () => {
      toast.success('Society basics saved');
      void qc.invalidateQueries({ queryKey: ['society', 'me'] });
      onNext();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <Card>
      <CardContent className="flex flex-col gap-vt-5">
        <header>
          <h2 className="text-vt-20 font-semibold text-vt-text">Society basics</h2>
          <p className="text-vt-13 text-vt-text-muted">
            Confirm the public-facing details for your society. You can change these later from Settings.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label htmlFor="ob-name">Society name</Label>
            <Input
              id="ob-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Green Meadows RWA"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="ob-addr">Address line 1</Label>
            <Input
              id="ob-addr"
              value={form.address_line1}
              onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
              placeholder="123 Park Road"
            />
          </div>
          <div>
            <Label htmlFor="ob-city">City</Label>
            <Input id="ob-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="ob-state">State</Label>
            <Input id="ob-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="ob-tz">Timezone</Label>
            <Select
              id="ob-tz"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="ob-email">Contact email</Label>
            <Input
              id="ob-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="committee@yoursociety.in"
            />
          </div>
          <div>
            <Label htmlFor="ob-phone">Contact phone</Label>
            <Input
              id="ob-phone"
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="+919876543210"
            />
            <p className="mt-vt-1 text-vt-11 text-vt-text-soft">
              Residents will see this number on overdue-bill banners and receipts.
            </p>
          </div>
        </div>

        <WizardFooter
          onBack={null}
          onSkip={onSkip}
          onNext={() => save.mutate()}
          nextLabel="Save and continue"
          nextLoading={save.isPending}
          nextDisabled={!form.name.trim()}
        />
      </CardContent>
    </Card>
  );
}

// ─── Step 2 — Blocks & Units ──────────────────────────────────────────────

function StepBlocksUnits({
  onNext,
  onSkip,
  onBack
}: {
  onNext: () => void;
  onSkip: () => void;
  onBack: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const blocksQuery = useQuery({
    queryKey: ['society', 'blocks'],
    queryFn: () => get<{ items: Block[] }>('/society/blocks')
  });

  const [newBlock, setNewBlock] = useState('');
  const [targetBlockId, setTargetBlockId] = useState<string>('');
  const [unitsCsv, setUnitsCsv] = useState('');

  useEffect(() => {
    if (blocksQuery.data?.items && blocksQuery.data.items.length > 0 && !targetBlockId) {
      setTargetBlockId(blocksQuery.data.items[0].id);
    }
  }, [blocksQuery.data?.items, targetBlockId]);

  const createBlock = useMutation({
    mutationFn: (name: string) => post<Block>('/society/blocks', { name }),
    onSuccess: (b) => {
      toast.success(`Block "${b.name}" added`);
      setNewBlock('');
      setTargetBlockId(b.id);
      void qc.invalidateQueries({ queryKey: ['society', 'blocks'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const bulkUnits = useMutation({
    mutationFn: (vars: { block_id: string; numbers: string[] }) =>
      post<{ created: number; skipped: number }>('/society/units', vars),
    onSuccess: (res) => {
      toast.success(`Added ${res.created} unit(s)${res.skipped ? `, ${res.skipped} skipped` : ''}`);
      setUnitsCsv('');
      void qc.invalidateQueries({ queryKey: ['society', 'units'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  function parseCsv(input: string): string[] {
    return input
      .split(/[\s,;]+/g)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  const parsedUnits = useMemo(() => parseCsv(unitsCsv), [unitsCsv]);
  const blocks = blocksQuery.data?.items ?? [];

  return (
    <Card>
      <CardContent className="flex flex-col gap-vt-5">
        <header>
          <h2 className="text-vt-20 font-semibold text-vt-text">Blocks &amp; units</h2>
          <p className="text-vt-13 text-vt-text-muted">
            Set up the physical layout. Add each tower/block, then bulk-paste flat numbers (comma, space, or newline
            separated).
          </p>
        </header>

        <section className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-4">
          <h3 className="text-vt-14 font-semibold text-vt-text">1. Add blocks / towers</h3>
          <div className="flex flex-wrap items-end gap-vt-3">
            <div className="grow">
              <Label htmlFor="new-block">Block name</Label>
              <Input
                id="new-block"
                value={newBlock}
                onChange={(e) => setNewBlock(e.target.value)}
                placeholder="A / Tower 1 / North Wing"
              />
            </div>
            <Button
              variant="secondary"
              disabled={!newBlock.trim() || createBlock.isPending}
              loading={createBlock.isPending}
              onClick={() => createBlock.mutate(newBlock.trim())}
            >
              Add block
            </Button>
          </div>
          {blocks.length > 0 ? (
            <ul className="flex flex-wrap gap-vt-2">
              {blocks.map((b) => (
                <li
                  key={b.id}
                  className="rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-1 text-vt-13"
                >
                  {b.name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-vt-13 text-vt-text-muted">No blocks yet — add at least one to enable units below.</p>
          )}
        </section>

        <section className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-4">
          <h3 className="text-vt-14 font-semibold text-vt-text">2. Bulk-import units</h3>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div>
              <Label htmlFor="target-block">Block</Label>
              <Select
                id="target-block"
                value={targetBlockId}
                onChange={(e) => setTargetBlockId(e.target.value)}
                disabled={blocks.length === 0}
              >
                <option value="">Select a block</option>
                {blocks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex items-end text-vt-12 text-vt-text-muted">
              {parsedUnits.length > 0 ? `${parsedUnits.length} unit number(s) parsed` : 'Awaiting input…'}
            </div>
          </div>
          <div>
            <Label htmlFor="units-csv">Unit numbers</Label>
            <Textarea
              id="units-csv"
              value={unitsCsv}
              onChange={(e) => setUnitsCsv(e.target.value)}
              rows={5}
              placeholder={'A-101, A-102, A-103\nA-201, A-202, A-203\n…'}
            />
            <p className="mt-vt-1 text-vt-12 text-vt-text-muted">
              Separate by commas, spaces, or newlines. Duplicates are skipped automatically.
            </p>
          </div>
          <div>
            <Button
              variant="secondary"
              disabled={!targetBlockId || parsedUnits.length === 0 || bulkUnits.isPending}
              loading={bulkUnits.isPending}
              onClick={() => bulkUnits.mutate({ block_id: targetBlockId, numbers: parsedUnits })}
            >
              Import {parsedUnits.length || ''} units
            </Button>
          </div>
        </section>

        <WizardFooter onBack={onBack} onSkip={onSkip} onNext={onNext} nextLabel="Continue" />
      </CardContent>
    </Card>
  );
}

// ─── Step 3 — Charge types ────────────────────────────────────────────────

function StepChargeTypes({
  onNext,
  onSkip,
  onBack
}: {
  onNext: () => void;
  onSkip: () => void;
  onBack: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['charge-types', 'onboarding'],
    queryFn: () => get<{ items: ChargeTypeRow[] }>('/charge-types', { params: { limit: 50 } })
  });

  // Local overrides keyed by charge-type id — we PATCH on Save only.
  const [edits, setEdits] = useState<Record<string, { label?: string; default_amount_inr?: number }>>({});

  const saveAll = useMutation({
    mutationFn: async () => {
      const entries = Object.entries(edits);
      for (const [id, body] of entries) {
        // eslint-disable-next-line no-await-in-loop -- small list (≤6), sequential keeps audit log readable
        await patch(`/charge-types/${id}`, body);
      }
      return entries.length;
    },
    onSuccess: (count) => {
      if (count > 0) toast.success(`Saved ${count} charge type(s)`);
      void qc.invalidateQueries({ queryKey: ['charge-types'] });
      onNext();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const rows = query.data?.items ?? [];

  return (
    <Card>
      <CardContent className="flex flex-col gap-vt-5">
        <header>
          <h2 className="text-vt-20 font-semibold text-vt-text">Charge types</h2>
          <p className="text-vt-13 text-vt-text-muted">
            These are the line-items that bills can be broken down into. We've seeded the six most common ones —
            adjust labels or default amounts now, or fine-tune later from /charge-types.
          </p>
        </header>

        {query.isLoading ? (
          <div className="flex items-center gap-vt-2 text-vt-13 text-vt-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading catalog…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-vt-13 text-vt-text-muted">
            No charge types in the catalog yet. Use the Charge Types page after onboarding to add some.
          </p>
        ) : (
          <ul className="flex flex-col gap-vt-2">
            {rows.map((c) => {
              const edit = edits[c.id] ?? {};
              const label = edit.label ?? c.label;
              const amount = edit.default_amount_inr ?? c.default_amount_inr;
              return (
                <li
                  key={c.id}
                  className="grid grid-cols-1 items-center gap-vt-3 rounded-vt-sm border border-vt-border bg-vt-surface-2 p-vt-3 md:grid-cols-[120px_1fr_180px]"
                >
                  <code className="text-vt-12 text-vt-text-muted">{c.code}</code>
                  <Input
                    aria-label={`Label for ${c.code}`}
                    value={label}
                    onChange={(e) => setEdits({ ...edits, [c.id]: { ...edit, label: e.target.value } })}
                  />
                  <div className="flex items-center gap-vt-2">
                    <span className="text-vt-13 text-vt-text-muted">₹</span>
                    <Input
                      aria-label={`Default amount for ${c.code}`}
                      type="number"
                      min={0}
                      value={amount}
                      onChange={(e) =>
                        setEdits({
                          ...edits,
                          [c.id]: { ...edit, default_amount_inr: Number(e.target.value) }
                        })
                      }
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <WizardFooter
          onBack={onBack}
          onSkip={onSkip}
          onNext={() => (Object.keys(edits).length > 0 ? saveAll.mutate() : onNext())}
          nextLabel={Object.keys(edits).length > 0 ? 'Save and continue' : 'Continue'}
          nextLoading={saveAll.isPending}
        />
      </CardContent>
    </Card>
  );
}

// ─── Step 4 — First admin (optional) ─────────────────────────────────────

function StepFirstAdmin({
  onNext,
  onSkip,
  onBack
}: {
  onNext: () => void;
  onSkip: () => void;
  onBack: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // No invite-admin endpoint exists yet (see SocietyAdmin model). This step
  // is informational and explicitly optional — we collect the info on-screen
  // as a reminder and surface a manual path. When the invite endpoint ships
  // we'll wire a POST here.
  return (
    <Card>
      <CardContent className="flex flex-col gap-vt-5">
        <header>
          <h2 className="text-vt-20 font-semibold text-vt-text">Invite a second admin</h2>
          <p className="text-vt-13 text-vt-text-muted">
            Optional but recommended. Adding a treasurer or secretary keeps society operations going if you're
            unavailable. You can do this any time from the Governance page.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <div>
            <Label htmlFor="ob-admin-name">Full name</Label>
            <Input id="ob-admin-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Asha Sharma" />
          </div>
          <div>
            <Label htmlFor="ob-admin-email">Email</Label>
            <Input
              id="ob-admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="asha@yoursociety.in"
            />
          </div>
          <div>
            <Label htmlFor="ob-admin-phone">Phone (E.164)</Label>
            <Input
              id="ob-admin-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+919876543210"
            />
          </div>
        </div>

        <div className="flex items-start gap-vt-3 rounded-vt-sm border border-vt-info/30 bg-vt-info/5 p-vt-3 text-vt-13 text-vt-text">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-vt-info" />
          <p>
            Invitations send via SMS OTP when the new admin first logs in. Skip this step now to do it later from
            Governance &gt; Committee Roles.
          </p>
        </div>

        <WizardFooter onBack={onBack} onSkip={onSkip} onNext={onNext} nextLabel="Continue" />
      </CardContent>
    </Card>
  );
}

// ─── Step 5 — Finish ──────────────────────────────────────────────────────

function StepFinish({
  societyName,
  onFinish,
  finishing,
  onBack,
  onNavigate
}: {
  societyName: string;
  onFinish: () => void;
  finishing: boolean;
  onBack: () => void;
  onNavigate: (to: '/residents' | '/bills' | '/elections' | '/dashboard') => void;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="flex flex-col gap-vt-5">
        <header className="flex items-center gap-vt-3">
          <CheckCircle2 className="h-8 w-8 text-vt-safe" />
          <div>
            <h2 className="text-vt-20 font-semibold text-vt-text">You're ready, {societyName}!</h2>
            <p className="text-vt-13 text-vt-text-muted">
              The essentials are in place. Click Finish to land on your dashboard — or jump straight into a common
              first task below.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
          <QuickLink
            title="Add residents"
            description="Map flats to owners and tenants. Send their first login OTP."
            onClick={() => onNavigate('/residents')}
          />
          <QuickLink
            title="Raise the first bill"
            description="Use your charge types catalog to issue maintenance bills."
            onClick={() => onNavigate('/bills')}
          />
          <QuickLink
            title="Start an election"
            description="Run committee elections with on-platform voting."
            onClick={() => onNavigate('/elections')}
          />
        </div>

        <div className="flex items-center justify-between gap-vt-3 pt-vt-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <Button onClick={onFinish} loading={finishing} loadingText="Finishing…">
            Finish &amp; go to dashboard
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickLink({
  title,
  description,
  onClick
}: {
  title: string;
  description: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-start gap-vt-1 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-4 text-left transition-colors hover:border-vt-primary-400 hover:bg-vt-surface"
    >
      <span className="text-vt-14 font-semibold text-vt-text">{title}</span>
      <span className="text-vt-12 text-vt-text-muted">{description}</span>
    </button>
  );
}

// ─── Shared footer ────────────────────────────────────────────────────────

function WizardFooter({
  onBack,
  onSkip,
  onNext,
  nextLabel,
  nextLoading,
  nextDisabled
}: {
  onBack: (() => void) | null;
  onSkip: () => void;
  onNext: () => void;
  nextLabel: string;
  nextLoading?: boolean;
  nextDisabled?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between border-t border-vt-border pt-vt-4">
      <div>
        {onBack ? (
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        ) : null}
      </div>
      <div className="flex items-center gap-vt-2">
        <Button variant="outline" onClick={onSkip}>
          Skip
        </Button>
        <Button onClick={onNext} loading={nextLoading} disabled={nextDisabled}>
          {nextLabel}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
